// @flow

import React, { useEffect, useState } from 'react';
import { observer, inject } from 'mobx-react';
import { useLocation, useNavigate } from 'react-router-dom';
import qs from 'qs';

import { initTrackPageView } from 'packs/analytics/trackPageView';

import paramsToQueryString from 'helpers/paramsToQueryString';
import UpdateQueryParamValues from 'helpers/UpdateQueryParamValues';
import RemoveQueryParamValues from 'helpers/RemoveQueryParamValues';
import updateLangSwitcher from 'helpers/updateLangSwitcher';
import flattenObject from 'helpers/flattenObject';
import searchEventParams from 'helpers/searchEventParams';

import { locale } from 'core/config';
import usePrev from 'core/hooks/usePrev';

import Pagination from 'components/Pagination';
import Preloader from 'components/Preloader';
import StatusBar from 'components/StatusBar';

import Search from './components/Search';
import Sidebar from './components/Sidebar';
import Companies from './components/Companies';
import Jobs from './components/Jobs';
import NoResults from './components/NoResults';

import store from './store';

type Props = {
  sessionStore: {
    getUser: Function,
    user: any,
    addJobAlert: Function,
    errors: any,
    isCurrentUser: boolean,
    handleLike: Function
  }
};

const JobsScreen = inject('sessionStore')(observer((props: Props) => {
  const { jobs, loading, totalRecords, relevantCompanies, perPage, counts, showFilters, showAlerts, showInput, toggleVisibility, places } = store;
  const { sessionStore: { user, addJobAlert, errors, isCurrentUser, handleLike, getUser } } = props;

  const location = useLocation();
  const prevLocation = usePrev(location);
  const navigate = useNavigate();

  const getSearch = () => {
    const {
      sort, page, q, category_ids, industry_ids, work_type_ids, entry_level_ids, job_language_ids,
      number_employees_group_ids, region_ids, place_id, distance
    } = flattenObject(qs.parse(location.search, { ignoreQueryPrefix: true }));
    return {
      sort, page, q, category_ids, industry_ids, work_type_ids, entry_level_ids, job_language_ids,
      number_employees_group_ids, region_ids, place_id, distance
    };
  };

  const [search, setSearch] = useState(getSearch());

  const urlParams = (searchParams: any) => {
    const {
      sort, page, q, category_ids, industry_ids, work_type_ids, entry_level_ids, job_language_ids,
      number_employees_group_ids, region_ids, place_id, distance
    } = searchParams;

    const customParamsObject = {
      page: {
        offset: Math.ceil((page - 1 || 0) * perPage),
        limit: perPage
      },
      filter: {
        query: q,
        category_ids: category_ids && category_ids.join(','),
        industry_ids: industry_ids && industry_ids.join(','),
        work_type_ids: work_type_ids && work_type_ids.join(','),
        entry_level_ids: entry_level_ids && entry_level_ids.join(','),
        job_language_ids: job_language_ids && job_language_ids.join(','),
        number_employees_group_ids: number_employees_group_ids && number_employees_group_ids.join(','),
        region_ids: region_ids && region_ids.join(','),
        place_id: place_id && place_id.toString(),
        distance: distance && +distance.toString()
      },
      locale
    };

    return { sort, ...customParamsObject };
  };

  useEffect(() => {
    const { getJobs, getRelevantCompanies, getJobsWithThirdParty } = store;
    const currentJobParams = urlParams(search);

    getUser();

    if (window.gon && window.gon.flipflop.third_party_job_search) {
      getJobsWithThirdParty(currentJobParams);
    } else getJobs(currentJobParams);
    getRelevantCompanies({ filter: { query: currentJobParams.filter.query } });

    setTimeout(() => initTrackPageView({
      event: 'view_search',
      properties: searchEventParams({ search })
    }), 300);
  }, []);

  useEffect(() => {
    const updatedSearch = getSearch();

    setSearch(updatedSearch);

    const { getJobs, getRelevantCompanies, getJobsWithThirdParty } = store;
    const currentJobParams = urlParams(updatedSearch);

    updateLangSwitcher(location.search);

    if (prevLocation && updatedSearch !== prevLocation.search) {
      if (window.gon && window.gon.flipflop.third_party_job_search) {
        getJobsWithThirdParty(currentJobParams);
      } else getJobs(currentJobParams);
      getRelevantCompanies({ filter: { query: currentJobParams.filter.query } });

      setTimeout(() => initTrackPageView({
        event: 'view_search',
        properties: searchEventParams({ search: updatedSearch })
      }), 300);
    }
  }, [location]);

  const currentPage = (searchParams: any) => +searchParams.page - 1 || 0;

  const updateSearch = (searchParams: {}): void => {
    navigate(`?${paramsToQueryString(searchParams)}`);
  };

  const pushToSearch = (searchParams: {}, action: 'update' | 'remove' = 'update'): void => {
    const queryString = paramsToQueryString(
      action === 'update'
        ? UpdateQueryParamValues(searchParams, search)
        : RemoveQueryParamValues(searchParams, search)
    );

    navigate(`?${queryString}`);
  };

  const handlePageClick = (page: any = {}) => {
    if (page >= 0) pushToSearch({ page });
  };

  const handleChange = (name: string) => (option: ?any) => {
    pushToSearch({ [name]: option && option.value, page: 0 });
  };

  return (
    <div className="container overflow-hidden">
      <Search
        search={search}
        updateSearch={updateSearch}
        pushToSearch={pushToSearch}
        urlSearch={location.search}
      />
      <div className="row">
        <Sidebar
          counts={counts}
          search={search}
          urlSearch={location.search}
          pushToSearch={pushToSearch}
          updateSearch={updateSearch}
          showFilters={showFilters}
          showAlerts={showAlerts}
          showInput={showInput}
          toggleVisibility={toggleVisibility}
          user={user}
          addJobAlert={addJobAlert}
          errors={errors}
          places={places}
        />
        {loading ? <Preloader/> :
          <div className="col-12 col-lg-8 col-xl-9">
            <div className="jobs-results__wrapper">
              {totalRecords < 1
                ? <NoResults isCurrentUser={isCurrentUser} toggleVisibility={toggleVisibility} showInput={showInput}/>
                : <>
                  <StatusBar totalRecords={totalRecords} handleChange={handleChange} searchParams={search}/>

                  <Companies
                    companies={relevantCompanies}
                    user={user}
                    isCurrentUser={isCurrentUser}
                    handleLike={handleLike}
                  />

                  <Jobs
                    jobs={jobs}
                    user={user}
                    isCurrentUser={isCurrentUser}
                    handleLike={handleLike}
                    toggleVisibility={toggleVisibility}
                    showInput={showInput}
                  />

                  <Pagination
                    pageCount={totalRecords}
                    perPage={perPage}
                    forcePage={currentPage(search)}
                    handlePageClick={handlePageClick}
                  />
                </>
              }
            </div>
          </div>
        }
      </div>
    </div>
  );
}));

export default JobsScreen;
